/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import {
  Backdrop,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Topheader from "../../Layout/header/header";
import { useEffect, useState, useCallback } from "react";
import { fetchAgreementDetails } from "../../Services/redux/agreement-details/AgreementDetailsActions";
import "./UserProfileDeveloper.css";
import DeveloperAgreement from "./developerAgreement";
import PopupDialog from "../../Components/PopupDialog";
import APIManager from "../../Services/ApiManager/ApiManager";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import Moment from "moment";
import { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";

import { fetchCountriesDetails } from "../../Services/redux/Country-details/CountryDetailsActions";
import { getConfig } from "../../config";
import { 
  loadSellerData, 
  loadOemAdminData, 
  normalizeSellerProfileResults,
  handleApiError, 
  updateSectionState,
  resetAllFormErrors,
  restoreProfileData,
  acceptAgreement
} from "./userProfileHelpers";

import { UserProfileHeader } from "./UserProfileHeader";
import { ProfileFormSection } from "./ProfileFormSection";
import { BusinessFormSection } from "./BusinessFormSection";

const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const urlRegex = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;
const registrationNumberRegex = /^[a-zA-Z0-9\- /]+$/;
const alphabetRegex = /^[a-zA-Z\s]+$/;

const isNonEmpty = (value) => typeof value === "string" ? value.trim().length > 0 : !!value;
const isAgreementFieldValid = (val) => val !== undefined && val !== null && val !== "";

const getApiFunction = (isSeller, isOemAdmin, isPatch = false) => {
  if (isPatch) {
    return isSeller ? APIManager.patchCognitoSelf : APIManager.patchCognitoAdminSelf;
  }
  return isSeller ? APIManager.getCognitoSelf : APIManager.getCognitoAdminSelf;
};

const getDataLoader = (isSeller) => isSeller ? loadSellerData : loadOemAdminData;

const buildAddressPayload = (isSeller, address) => (
  isSeller
    ? { companyAddress: address }
    : { address1: address }
);

const renderDialogActions = ({
  agreement_accepted,
  disableSubmitAgreement,
  allBusinessValid,
  allProfileValid,
  agreementDetails,
  agreementDetailsData,
  agreementStatus,
  handleAgreementChange,
  handleDisagree,
  handleAcceptAgreement
}) => {
  const currentAgreementDetails = agreementDetails || agreementDetailsData;
  const currentStatus = currentAgreementDetails?.status;
  const hasNewUpdate = currentAgreementDetails?.isNewAgreementAvailable;
  const showActions = !agreementStatus || currentStatus === 'NOT_ACCEPTED' || currentStatus === 'NEW_UPDATE_AVAILABLE' || currentStatus === 'REJECTED' || hasNewUpdate;

  if (!showActions) return null;

  const agreementTooltipMessage = (() => {
    if (!agreement_accepted || !disableSubmitAgreement) return "";
    if (!allBusinessValid && !allProfileValid) {
      return "Complete all required fields in \"Profile Info\" and \"Business Info\" to enable this action.";
    }
    if (allBusinessValid && !allProfileValid) {
      return "Complete all required fields in \"Profile Info\" to enable this action.";
    }
    if (!allBusinessValid && allProfileValid) {
      return "Complete all required fields in \"Business Info\" to enable this action.";
    }
    return "";
  })();

  return (
    <DialogActions style={{ justifyContent: "space-between" }}>
      <div className="checkAgreeCls">
        <Checkbox
          checked={agreement_accepted}
          size="small"
          onChange={handleAgreementChange}
          slotProps={{ input: { 'aria-label': 'controlled' } }}
        />
        <span>I agree to the updated Terms</span>
      </div>
      <div>
        <Button
          className="editBtnCls"
          variant="outlined"
          onClick={handleDisagree}
        >
          Disagree
        </Button>
        &nbsp;&nbsp;
        <Tooltip
          title={agreementTooltipMessage}
          placement="top"
          arrow
          open={Boolean(agreementTooltipMessage)}
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: 178,
                whiteSpace: "normal",
                fontFamily: "Roboto, sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0.25px",
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
            },
          }}
        >
          <span>
            <Button
              disabled={disableSubmitAgreement}
              onClick={handleAcceptAgreement}
              style={{
                background: "221.2 83.2% 53.3%",
                textTransform: "none",
                height: "32px",
                width: "116px",
                fontWeight: "500",
                fontSize: "14px",
                lineHeight: "16.41px",
                boxShadow: "none",
              }}
              variant="contained"
            >
              Accept
            </Button>
          </span>
        </Tooltip>
      </div>
    </DialogActions>
  );
};

const renderAcceptedDialog = ({
  agreementDetails,
  agreementDetailsData,
  agreementStatus,
  agreementDate,
  Moment
}) => {
  const currentAgreementDetails = agreementDetails || agreementDetailsData;
  const currentStatus = currentAgreementDetails?.status;
  const hasNewUpdate = currentAgreementDetails?.isNewAgreementAvailable;
  const showAccepted = agreementStatus && currentStatus === 'ACCEPTED' && !hasNewUpdate;

  if (!showAccepted) return null;

  return (
    <DialogActions className="acceptedDialogCls">
      <CheckIcon className="checkAcceptedCls" />
      <span className="checkBoxTextCls">
        Accepted on &nbsp;
        {Moment(agreementDate).format("MMM DD YYYY")}
      </span>
    </DialogActions>
  );
};

const createValidators = (setters) => ({
  corporateContactName: (value) => {
    if (!value) { setters.setInputCrpNameErrMsg("Contact Name is required"); return false; }
    setters.setInputCrpNameErrMsg(""); return true;
  },
  corporateContactEmail: (value) => {
    if (!value) { setters.setInputCrpEmailErrMsg("Contact Email is required"); return false; }
    if (!emailRegex.test(value)) { setters.setInputCrpEmailErrMsg("Please enter a valid email address"); return false; }
    setters.setInputCrpEmailErrMsg(""); return true;
  },
  address: (value) => {
    if (!value) { setters.setInputAddressErrMsg("Address is required"); return false; }
    if (value.length <= 10) { setters.setInputAddressErrMsg(`Entered: ${value.length}, Required more than: 10`); return false; }
    setters.setInputAddressErrMsg(""); return true;
  },
  companyName: (value) => {
    if (!value) { setters.setInputCompNameErrMsg("Business Entity name is required"); return false; }
    setters.setInputCompNameErrMsg(""); return true;
  },
  companyWebsite: (value) => {
    if (!value) { setters.setInputCompWebsiteErrMsg("Company Website is required"); return false; }
    if (!urlRegex.test(value)) { setters.setInputCompWebsiteErrMsg("Please enter a valid URL"); return false; }
    setters.setInputCompWebsiteErrMsg(""); return true;
  },
  privacyPolicy: (value) => {
    if (!value) { setters.setInputPrivacyLinkErrMsg("Privacy Policy is required"); return false; }
    if (!urlRegex.test(value)) { setters.setInputPrivacyLinkErrMsg("Please enter a valid URL"); return false; }
    setters.setInputPrivacyLinkErrMsg(""); return true;
  },
  registrationNumber: (value) => {
    if (!value) { setters.setInputRegNumberErrMsg("Registration Number is required"); return false; }
    if (!registrationNumberRegex.test(value)) { setters.setInputRegNumberErrMsg("Registration number must contain only alphanumeric characters, hyphens, spaces, or slashes"); return false; }
    setters.setInputRegNumberErrMsg(""); return true;
  },
  countryOfRegistration: (value) => {
    if (!value) { setters.setInputCountryRegErrMsg("Country of Registration is required"); return false; }
    if (!alphabetRegex.test(value)) { setters.setInputCountryRegErrMsg("Country name must contain only letters"); return false; }
    setters.setInputCountryRegErrMsg(""); return true;
  },
  corporateCompanyZip: (value) => {
    if (!value) { setters.setInputCorporateCompanyZip("Company Zip is required"); return false; }
    setters.setInputCorporateCompanyZip(""); return true;
  },
  registrationCourt: (value) => {
    if (!value) { setters.setInputRegistrationCourt("Registration Court is required"); return false; }
    setters.setInputRegistrationCourt(""); return true;
  },
  corporateCompanyCity: (value) => {
    if (!value) { setters.setInputCorporateCompanyCity("Corporate Company City is required"); return false; }
    setters.setInputCorporateCompanyCity(""); return true;
  },
  corporateCompanyState: (value) => {
    if (!value) { setters.setInputCorporateCompanyState("Corporate Company State is required"); return false; }
    setters.setInputCorporateCompanyState(""); return true;
  },
  companyEmail: (value) => {
    if (!value) { setters.setInputCompanyEmailErrMsg("Company Email is required"); return false; }
    if (!emailRegex.test(value)) { setters.setInputCompanyEmailErrMsg("Please enter a valid email address"); return false; }
    setters.setInputCompanyEmailErrMsg(""); return true;
  },
  countryOfCompany: (value) => {
    if (!value) { setters.setInputCountryCompanyErrMsg("Country of Company is required"); return false; }
    setters.setInputCountryCompanyErrMsg(""); return true;
  },
});

const fieldNameMap = {
  "Corporate Contact Name": "corporateContactName",
  "Corporate Contact Email": "corporateContactEmail",
  "Address": "address",
  "Company Name": "companyName",
  "Company Website": "companyWebsite",
  "Privacy Policy": "privacyPolicy",
  "Registration Number": "registrationNumber",
  "Country of Registration": "countryOfRegistration",
  "Corporate Company Zip": "corporateCompanyZip",
  "Registration Court": "registrationCourt",
  "Corporate Company City": "corporateCompanyCity",
  "Corporate Company State": "corporateCompanyState",
  "Company Email": "companyEmail",
  "Country of Company": "countryOfCompany",
};

const restoreAndResetOnSectionChange = ({
  profileData,
  corporateContactName,
  corporateContactEmail,
  phoneNumber,
  address,
  companyRegNumber,
  countryOfReg,
  companyName,
  companyWebsite,
  privacyPolicy,
  registrationCourt,
  corporateCompanyState,
  corporateCompanyCity,
  corporateCompanyZip,
  countryOfCompany,
  companyPhoneNumber,
  companyEmail,
  setCorporateContactName,
  setCorporateContactEmail,
  setPhoneNumber,
  setAddress,
  setCompanyRegNumber,
  setCountryOfReg,
  setCompanyName,
  setCompanyWebsite,
  setPrivacyPolicy,
  setAgreementStatus,
  setAgreementDate,
  setRegistrationCourt,
  setCorporateCompanyState,
  setCorporateCompanyCity,
  setCorporateCompanyZip,
  setCountryOfCompany,
  setCompanyPhoneNumber,
  setCompanyEmail,
  setProfileReadOnly,
  setShowSaveFlag,
  setInputCrpNameErrMsg,
  setInputCrpEmailErrMsg,
  setInputAddressErrMsg,
  setInputCompNameErrMsg,
  setInputCompWebsiteErrMsg,
  setInputPrivacyLinkErrMsg,
  setInputRegNumberErrMsg,
  setInputCountryRegErrMsg,
  setInputCorporateCompanyZip,
  setInputRegistrationCourt,
  setInputCorporateCompanyCity,
  setInputCorporateCompanyState,
  setInputCompanyEmailErrMsg,
  setInputCountryCompanyErrMsg,
}) => {
  console.log("profileData=====>", profileData);
  
  const currentState = {
    corporateContactName, corporateContactEmail, phoneNumber, address,
    companyRegNumber, countryOfReg, companyName, companyWebsite, privacyPolicy,
    registrationCourt, corporateCompanyState, corporateCompanyCity,
    corporateCompanyZip, countryOfCompany, companyPhoneNumber, companyEmail
  };
  
  const restoreSetters = {
    setCorporateContactName, setCorporateContactEmail, setPhoneNumber, setAddress,
    setCompanyRegNumber, setCountryOfReg, setCompanyName, setCompanyWebsite,
    setPrivacyPolicy, setAgreementStatus, setAgreementDate, setRegistrationCourt,
    setCorporateCompanyState, setCorporateCompanyCity, setCorporateCompanyZip,
    setCountryOfCompany, setCompanyPhoneNumber, setCompanyEmail, setProfileReadOnly, setShowSaveFlag
  };
  
  restoreProfileData(profileData, currentState, restoreSetters);
  setProfileReadOnly(true);
  setShowSaveFlag(false);
  
  const errorSetters = {
    setInputCrpNameErrMsg, setInputCrpEmailErrMsg, setInputAddressErrMsg,
    setInputCompNameErrMsg, setInputCompWebsiteErrMsg, setInputPrivacyLinkErrMsg,
    setInputRegNumberErrMsg, setInputCountryRegErrMsg, setInputCorporateCompanyZip,
    setInputRegistrationCourt, setInputCorporateCompanyCity, setInputCorporateCompanyState,
    setInputCompanyEmailErrMsg, setInputCountryCompanyErrMsg
  };
  
  resetAllFormErrors(errorSetters);
};

const handleAcceptAgreementSuccess = (
  result,
  setAgreementDetails,
  setAgreementStatus,
  setAgreementDate,
  setProfileData,
  setLoader,
  setOpen
) => {
  setAgreementDetails(result.details);
  setAgreementStatus(true);
  setAgreementDate(result.acceptDate);
  if (result.results) {
    setProfileData([...result.results]);
  }
  sessionStorage.removeItem("agreementPending");
  setLoader(false);
  setOpen(false);
};

const handleAcceptAgreementError = (setLoader, setOpen, setErrorMessage, setHeaderMessage, setOpenErrorPopup, errorMsg) => {
  setLoader(false);
  setOpen(false);
  if (setErrorMessage && setHeaderMessage && setOpenErrorPopup) {
    setErrorMessage(errorMsg || "Failed to accept agreement");
    setHeaderMessage(errorMsg === "jwt expired" ? "401 Token Expired" : "Error");
    setOpenErrorPopup(true);
  }
};

const handleAcceptAgreementExternal = async ({
  corporateContactName,
  corporateContactEmail,
  address,
  phoneNumber,
  privacyPolicy,
  companyRegNumber,
  countryOfReg,
  companyWebsite,
  companyName,
  corporateCompanyZip,
  corporateCompanyCity,
  corporateCompanyState,
  countryOfCompany,
  companyPhoneNumber,
  companyEmail,
  registrationCourt,
  agreementDetails,
  agreementDetailsData,
  isOemAdmin,
  setLoader,
  getConfig,
  acceptAgreement,
  dispatch,
  fetchAgreementDetails,
  APIManager,
  Moment,
  handleAcceptAgreementSuccess,
  handleAcceptAgreementError,
  setAgreementDetails,
  setAgreementStatus,
  setAgreementDate,
  setProfileData,
  setOpen,
  setErrorMessage,
  setHeaderMessage,
  setOpenErrorPopup
}) => {
  const isSeller = getConfig().REACT_APP_PORTAL === "seller";
  if (!isSeller) return;
  
  setLoader(true);

  const addressPayload = buildAddressPayload(isSeller, address);
  
  const profileData = {
    corporateContactName,
    corporateContactEmail,
    ...addressPayload,
    phoneNumber,
    companyPrivacyPolicy: privacyPolicy,
    companyRegNumber,
    countryOfReg,
    companyWebsite,
    companyEntityName: companyName,
    companyZip: corporateCompanyZip,
    companyCity: corporateCompanyCity,
    companyState: corporateCompanyState,
    companyCountry: countryOfCompany,
    companyPhoneNumber,
    companyEmail,
    registrationCourt
  };

  const result = await acceptAgreement(
    agreementDetails, 
    agreementDetailsData, 
    profileData, 
    { dispatch, fetchAgreementDetails, APIManager, isSeller, Moment }
  );

  if (result.success) {
    handleAcceptAgreementSuccess(
      result,
      setAgreementDetails,
      setAgreementStatus,
      setAgreementDate,
      setProfileData,
      setLoader,
      setOpen
    );
  } else {
    handleAcceptAgreementError(setLoader, setOpen, setErrorMessage, setHeaderMessage, setOpenErrorPopup, result.error);
  }
};

const handleProfileSaveSuccess = ({
  json,
  profileSection,
  isSeller,
  savedAddress,
  setProfileData,
  setLoader,
  setReadOnlyValue,
  setProfileReadOnly,
  setShowSaveFlag,
  setBusinessReadOnly,
  setShowBusinessFlag,
  setSuccessNotification
}) => {
  const nextResults = isSeller
    ? normalizeSellerProfileResults(json?.results, savedAddress)
    : [...(json?.results || [])];

  setProfileData(nextResults);
  setLoader(false);
  setReadOnlyValue(true);
  setProfileReadOnly(true);
  setSuccessNotification(isSeller ? 'seller' : 'default');
  
  if (profileSection) {
    setShowSaveFlag(false);
    return;
  }
  
  setBusinessReadOnly(true);
  setShowBusinessFlag(false);
};

const handleViewDeveloperAgreementExternal = async ({
  getConfig,
  dispatch,
  fetchAgreementDetails,
  allBusinessValid,
  allProfileValid,
  agreement_accepted,
  setDisableSubmitAgreement,
  setOpen
}) => {
  const isSeller = getConfig().REACT_APP_PORTAL === "seller";
  if (!isSeller) return;
  
  dispatch(fetchAgreementDetails());
  const allValid = allBusinessValid && allProfileValid && agreement_accepted;
  setDisableSubmitAgreement(!allValid);
  setOpen(true);
};

const goToSectionExternal = ({
  index,
  readOnlyValue,
  setProfileSection,
  setBusinessSection,
  setShowBusinessFlag,
  setBusinessReadOnly,
  setProfileReadOnly,
  setShowSaveFlag,
  profileData,
  corporateContactName,
  corporateContactEmail,
  phoneNumber,
  address,
  companyRegNumber,
  countryOfReg,
  companyName,
  companyWebsite,
  privacyPolicy,
  registrationCourt,
  corporateCompanyState,
  corporateCompanyCity,
  corporateCompanyZip,
  countryOfCompany,
  companyPhoneNumber,
  companyEmail,
  setCorporateContactName,
  setCorporateContactEmail,
  setPhoneNumber,
  setAddress,
  setCompanyRegNumber,
  setCountryOfReg,
  setCompanyName,
  setCompanyWebsite,
  setPrivacyPolicy,
  setAgreementStatus,
  setAgreementDate,
  setRegistrationCourt,
  setCorporateCompanyState,
  setCorporateCompanyCity,
  setCorporateCompanyZip,
  setCountryOfCompany,
  setCompanyPhoneNumber,
  setCompanyEmail,
  setInputCrpNameErrMsg,
  setInputCrpEmailErrMsg,
  setInputAddressErrMsg,
  setInputCompNameErrMsg,
  setInputCompWebsiteErrMsg,
  setInputPrivacyLinkErrMsg,
  setInputRegNumberErrMsg,
  setInputCountryRegErrMsg,
  setInputCorporateCompanyZip,
  setInputRegistrationCourt,
  setInputCorporateCompanyCity,
  setInputCorporateCompanyState,
  setInputCompanyEmailErrMsg,
  setInputCountryCompanyErrMsg,
}) => {
  const sectionSetters = {
    setProfileSection, setBusinessSection, setShowBusinessFlag, setBusinessReadOnly,
    setProfileReadOnly, setShowSaveFlag
  };
  
  updateSectionState(index, readOnlyValue, sectionSetters);
  
  if (readOnlyValue) {
    restoreAndResetOnSectionChange({
      profileData,
      corporateContactName,
      corporateContactEmail,
      phoneNumber,
      address,
      companyRegNumber,
      countryOfReg,
      companyName,
      companyWebsite,
      privacyPolicy,
      registrationCourt,
      corporateCompanyState,
      corporateCompanyCity,
      corporateCompanyZip,
      countryOfCompany,
      companyPhoneNumber,
      companyEmail,
      setCorporateContactName,
      setCorporateContactEmail,
      setPhoneNumber,
      setAddress,
      setCompanyRegNumber,
      setCountryOfReg,
      setCompanyName,
      setCompanyWebsite,
      setPrivacyPolicy,
      setAgreementStatus,
      setAgreementDate,
      setRegistrationCourt,
      setCorporateCompanyState,
      setCorporateCompanyCity,
      setCorporateCompanyZip,
      setCountryOfCompany,
      setCompanyPhoneNumber,
      setCompanyEmail,
      setProfileReadOnly,
      setShowSaveFlag,
      setInputCrpNameErrMsg,
      setInputCrpEmailErrMsg,
      setInputAddressErrMsg,
      setInputCompNameErrMsg,
      setInputCompWebsiteErrMsg,
      setInputPrivacyLinkErrMsg,
      setInputRegNumberErrMsg,
      setInputCountryRegErrMsg,
      setInputCorporateCompanyZip,
      setInputRegistrationCourt,
      setInputCorporateCompanyCity,
      setInputCorporateCompanyState,
      setInputCompanyEmailErrMsg,
      setInputCountryCompanyErrMsg,
    });
  }
};

const loadInitialData = ({
  isSeller,
  isOemAdmin,
  getApiFunction,
  getDataLoader,
  handleApiError,
  setProfileData,
  setAccount,
  setCorporateContactName,
  setCorporateContactEmail,
  setPhoneNumber,
  setAddress,
  setPrivacyPolicy,
  setCompanyRegNumber,
  setCountryOfReg,
  setCompanyName,
  setCompanyWebsite,
  setAgreementDetails,
  setAgreementStatus,
  setAgreementDate,
  setRegistrationCourt,
  setCorporateCompanyState,
  setCorporateCompanyCity,
  setCorporateCompanyZip,
  setCountryOfCompany,
  setCompanyPhoneNumber,
  setCompanyEmail,
  setComponentName,
  setErrorMessage,
  setHeaderMessage,
  setOpenErrorPopup,
  setLoader,
  setIsVwGroupSeller
}) => {
  const dataSetters = {
    setProfileData, setAccount, setCorporateContactName, setCorporateContactEmail,
    setPhoneNumber, setAddress, setPrivacyPolicy, setCompanyRegNumber,
    setCountryOfReg, setCompanyName, setCompanyWebsite, setAgreementDetails,
    setAgreementStatus, setAgreementDate, setRegistrationCourt,
    setCorporateCompanyState, setCorporateCompanyCity, setCorporateCompanyZip,
    setCountryOfCompany, setCompanyPhoneNumber, setCompanyEmail
  };
  
  const errorSetters = {
    setComponentName, setErrorMessage, setHeaderMessage, setOpenErrorPopup, setLoader
  };
  
  const api = getApiFunction(isSeller, isOemAdmin, false);
  const loader = getDataLoader(isSeller);
  
  api()
    .then((json) => {
      loader(json, dataSetters);
      const selfCompanyName = json?.results?.[0]?.companyName || json?.results?.[0]?.dsaInfo?.businessEntities?.[0]?.entityName || "";
      const isVwGroupCompany = String(selfCompanyName).trim().toLowerCase() === "vwgroup";
      setIsVwGroupSeller(isSeller && isVwGroupCompany);
      setLoader(false);
    })
    .catch((error) => handleApiError(error, errorSetters));
};

const handleProfileSaveExternal = ({
  corporateContactName,
  corporateContactEmail,
  phoneNumber,
  privacyPolicy,
  companyName,
  companyWebsite,
  address,
  corporateCompanyZip,
  corporateCompanyCity,
  corporateCompanyState,
  countryOfCompany,
  companyPhoneNumber,
  companyEmail,
  companyRegNumber,
  countryOfReg,
  registrationCourt,
  isDsaEditMode,
  isSeller,
  isOemAdmin,
  getApiFunction,
  profileSection,
  setProfileData,
  setLoader,
  setReadOnlyValue,
  setProfileReadOnly,
  setShowSaveFlag,
  setBusinessReadOnly,
  setShowBusinessFlag,
  setSuccessNotification,
  setErrorMessage,
  setHeaderMessage,
  setOpenErrorPopup,
  handleProfileSaveSuccess
}) => {
  const addressPayload = buildAddressPayload(isSeller, address);

  const saveProfileData = {
    corporateContactName,
    corporateContactEmail,
    phoneNumber,
    companyPrivacyPolicy: privacyPolicy,
    companyName,
    companyWebsite,
    companyEntityName: companyName,
    ...addressPayload,
    companyZip: corporateCompanyZip,
    companyCity: corporateCompanyCity,
    companyState: corporateCompanyState,
    companyCountry: countryOfCompany,
    companyPhoneNumber,
    companyEmail,
    companyRegNumber,
    countryOfReg,
    registrationCourt,
    ...((isDsaEditMode || isSeller) && { dsaReviewed: true }),
  };

  const api = getApiFunction(isSeller, isOemAdmin, true);
  api(saveProfileData).then((json) => 
    handleProfileSaveSuccess({
      json,
      profileSection,
      isSeller,
      savedAddress: address,
      setProfileData,
      setLoader,
      setReadOnlyValue,
      setProfileReadOnly,
      setShowSaveFlag,
      setBusinessReadOnly,
      setShowBusinessFlag,
      setSuccessNotification,
    })
  ).catch((error) => {
    setLoader(false);
    if (setErrorMessage && setHeaderMessage && setOpenErrorPopup) {
      setErrorMessage(error.message);
      setHeaderMessage(error.message === "jwt expired" ? "401 Token Expired" : "Error");
      setOpenErrorPopup(true);
    }
  });
};

const loadProfileFromDataExternal = ({
  data,
  setCorporateContactName,
  setCorporateContactEmail,
  setPhoneNumber,
  setAddress,
  setPrivacyPolicy,
  setCompanyRegNumber,
  setCountryOfReg,
  setCompanyName,
  setCompanyWebsite,
  setAgreementStatus,
  setAgreementDate,
  setRegistrationCourt,
  setCorporateCompanyState,
  setCorporateCompanyCity,
  setCorporateCompanyZip,
  setCountryOfCompany,
  setCompanyPhoneNumber,
  setCompanyEmail
}) => {
  if (data?.length) {
    const be = data[0]?.dsaInfo?.businessEntities?.[0] || {};
    const address = be.address || data[0]?.companyAddress || data[0]?.address1 || "";
    setCorporateContactName(data[0]?.corporateContactName || "");
    setCorporateContactEmail(data[0]?.corporateContactEmail || "");
    setPhoneNumber(data[0]?.phoneNumber || "");
    setAddress(address);
    setPrivacyPolicy(be.privacyPolicy || "");
    setCompanyRegNumber(be.companyRegNumber || "");
    setCountryOfReg(be.countryOfReg || "");
    setCompanyName(be.entityName || "");
    setCompanyWebsite(be.website || "");
    setAgreementStatus(data[0]?.igniteStoreAgreementStatus || false);
    setAgreementDate(data[0]?.igniteStoreAgreementDate || "");
    setRegistrationCourt(be.registrationCourt || "");
    setCorporateCompanyState(be.state || "");
    setCorporateCompanyCity(be.city || "");
    setCorporateCompanyZip(be.zip || "");
    setCountryOfCompany(be.country || "");
    setCompanyPhoneNumber(be.phoneNumber || "");
    setCompanyEmail(be.email || "");
  }
};

const validateInputExternal = (value, fieldName, validators, fieldNameMap) => {
  const validatorKey = fieldNameMap[fieldName];
  const validator = validators[validatorKey];
  if (!validator) return undefined;
  return validator(value);
};

const computeProfileMandatoryValid = ({
  isIdpLogin, corporateContactName, inputCrpNameErrMsg,
  corporateContactEmail, inputCrpEmailErrMsg,
  phoneNumber, address, inputAddressErrMsg,
  privacyPolicy, inputPrivacyLinkErrMsg
}) => {
  return (
    (isIdpLogin || (isNonEmpty(corporateContactName) && inputCrpNameErrMsg === "")) &&
    (isIdpLogin || (isNonEmpty(corporateContactEmail) && inputCrpEmailErrMsg === "")) &&
    (isIdpLogin || (isNonEmpty(phoneNumber) && isValidPhoneNumber(phoneNumber) === true)) &&
    isNonEmpty(address) &&
    inputAddressErrMsg === "" &&
    (isIdpLogin || (isNonEmpty(privacyPolicy) && inputPrivacyLinkErrMsg === ""))
  );
};

const computeBusinessMandatoryValid = ({
  companyName, inputCompNameErrMsg, companyWebsite, inputCompWebsiteErrMsg,
  countryOfReg, inputCountryRegErrMsg, companyRegNumber, inputRegNumberErrMsg,
  registrationCourt, inputRegistrationCourt, corporateCompanyCity, inputCorporateCompanyCity,
  corporateCompanyState, inputCorporateCompanyState, corporateCompanyZip, inputCorporateCompanyZip,
  countryOfCompany, inputCountryCompanyErrMsg, companyPhoneNumber, companyEmail, inputCompanyEmailErrMsg
}) => {
  return (
    isNonEmpty(companyName) &&
    inputCompNameErrMsg === "" &&
    isNonEmpty(companyWebsite) &&
    inputCompWebsiteErrMsg === "" &&
    isNonEmpty(countryOfReg) &&
    inputCountryRegErrMsg === "" &&
    isNonEmpty(companyRegNumber) &&
    inputRegNumberErrMsg === "" &&
    isNonEmpty(registrationCourt) &&
    inputRegistrationCourt === "" &&
    isNonEmpty(corporateCompanyCity) &&
    inputCorporateCompanyCity === "" &&
    isNonEmpty(corporateCompanyState) &&
    inputCorporateCompanyState === "" &&
    isNonEmpty(corporateCompanyZip) &&
    inputCorporateCompanyZip === "" &&
    isNonEmpty(countryOfCompany) &&
    inputCountryCompanyErrMsg === "" &&
    isNonEmpty(companyPhoneNumber) &&
    isValidPhoneNumber(companyPhoneNumber) === true &&
    isNonEmpty(companyEmail) &&
    inputCompanyEmailErrMsg === ""
  );
};

export default function UserProfileDeveloper() {
  const dispatch = useDispatch();
  const location = useLocation();
  const [loader, setLoader] = useState(false);
  const [account, setAccount] = useState("");
  const [corporateContactName, setCorporateContactName] = useState("");
  const [corporateContactEmail, setCorporateContactEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [companyPhoneNumber, setCompanyPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [companyWebsite, setCompanyWebsite] = useState("");
  const [privacyPolicy, setPrivacyPolicy] = useState("");
  const [agreementStatus, setAgreementStatus] = useState(false);
  const [agreementDate, setAgreementDate] = useState("");
  const [agreementDetails, setAgreementDetails] = useState(null);
  const [companyRegNumber, setCompanyRegNumber] = useState("");
  const [inputRegNumberErrMsg, setInputRegNumberErrMsg] = useState("");
  const [inputCorporateCompanyZip, setInputCorporateCompanyZip] = useState("");
  const [inputCountryRegErrMsg, setInputCountryRegErrMsg] = useState("");
  const [inputCompNameErrMsg, setInputCompNameErrMsg] = useState("");
  const [inputCompWebsiteErrMsg, setInputCompWebsiteErrMsg] = useState("");
  const [inputRegistrationCourt, setInputRegistrationCourt] = useState("");
  const [countryOfReg, setCountryOfReg] = useState("");
  const [countryOfCompany, setCountryOfCompany] = useState("");
  const [readOnlyValue, setReadOnlyValue] = useState(!location.state?.editMode);
  const [showSaveFlag, setShowSaveFlag] = useState(location.state?.editMode ?? false);
  const [showBusinessFlag, setShowBusinessFlag] = useState(false);
  const [disableSubmitAgreement, setDisableSubmitAgreement] = useState(false);
  const [agreement_accepted, setAgreement_accepted] = useState(false);
  const [inputCrpNameErrMsg, setInputCrpNameErrMsg] = useState("");
  const [inputCrpEmailErrMsg, setInputCrpEmailErrMsg] = useState("");
  const [inputAddressErrMsg, setInputAddressErrMsg] = useState("");
  const [inputPrivacyLinkErrMsg, setInputPrivacyLinkErrMsg] = useState("");

  const [profileSection, setProfileSection] = useState(true);
  const [businessSection, setBusinessSection] = useState(false);
  const [profileReadOnly, setProfileReadOnly] = useState(!location.state?.editMode);
  const [businessReadOnly, setBusinessReadOnly] = useState(true);
  const [corporateCompanyCity, setCorporateCompanyCity] = useState("");
  const [corporateCompanyState, setCorporateCompanyState] = useState("");
  const [corporateCompanyZip, setCorporateCompanyZip] = useState("");
  const [inputCorporateCompanyCity, setInputCorporateCompanyCity] = useState("");
  const [inputCorporateCompanyState, setInputCorporateCompanyState] = useState("");
  const [inputCompanyEmailErrMsg, setInputCompanyEmailErrMsg] = useState("");
  const [inputCountryCompanyErrMsg, setInputCountryCompanyErrMsg] = useState("");
  const [profileData, setProfileData] = useState([]);
  const [registrationCourt, setRegistrationCourt] = useState("");
  const [open, setOpen] = useState(false);
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");
  const [successNotification, setSuccessNotification] = useState('');
  const [isVwGroupSeller, setIsVwGroupSeller] = useState(false);
  const countries = useSelector((state) => state.countryDetails.countries);
  const agreementDetailsData = useSelector((state) => state.agreementDetails.agreementDetails);

  const isOemAdmin = getConfig().REACT_APP_PORTAL === "oem-admin";
  const isSeller = getConfig().REACT_APP_PORTAL === "seller";
  const isIdpLogin = sessionStorage.getItem("idpLogin") === "true";

  const validators = createValidators({
    setInputCrpNameErrMsg, setInputCrpEmailErrMsg, setInputAddressErrMsg,
    setInputCompNameErrMsg, setInputCompWebsiteErrMsg, setInputPrivacyLinkErrMsg,
    setInputRegNumberErrMsg, setInputCountryRegErrMsg, setInputCorporateCompanyZip,
    setInputRegistrationCourt, setInputCorporateCompanyCity, setInputCorporateCompanyState,
    setInputCompanyEmailErrMsg, setInputCountryCompanyErrMsg
  });

  const allBusinessValid =
    isAgreementFieldValid(companyRegNumber) &&
    isAgreementFieldValid(countryOfReg) &&
    isAgreementFieldValid(companyName) &&
    isAgreementFieldValid(companyWebsite) &&
    isAgreementFieldValid(registrationCourt) &&
    isAgreementFieldValid(corporateCompanyState) &&
    isAgreementFieldValid(corporateCompanyCity) &&
    isAgreementFieldValid(corporateCompanyZip) &&
    isAgreementFieldValid(countryOfCompany) &&
    isAgreementFieldValid(companyPhoneNumber) &&
    isAgreementFieldValid(companyEmail);

  const allProfileValid =
    isAgreementFieldValid(corporateContactEmail) &&
    isAgreementFieldValid(corporateContactName) &&
    isAgreementFieldValid(phoneNumber) &&
    isAgreementFieldValid(address) &&
    isAgreementFieldValid(privacyPolicy);

  const handleClose = () => {
    setAgreement_accepted(false);
    setDisableSubmitAgreement(false);
    setOpen(false);
  };

  const handleDisagree = handleClose;

  const handleEdit = () => {
    const isProfile = profileSection;
    setProfileReadOnly(!isProfile);
    setBusinessReadOnly(isProfile);
    setShowSaveFlag(isProfile);
    setShowBusinessFlag(!isProfile);
    setReadOnlyValue(false);
  };

  const handleCountryChange = (event, isRegistration) => {
    const countryData = {
      true: { setter: setCountryOfReg, fieldName: "Country of Registration" },
      false: { setter: setCountryOfCompany, fieldName: "Country of Company" }
    };
    const countryName = countries.find((country) => country.id === event.target.value)?.name || event.target.value;
    const { setter, fieldName } = countryData[isRegistration];
    setter(countryName);
    validateInputExternal(countryName, fieldName, validators, fieldNameMap);
  };

  const handleFieldChange = (setter, fieldName) => (event) => {
    setter(event.target.value);
    validateInputExternal(event.target.value, fieldName, validators, fieldNameMap);
  };

  const clearAllErrors = () => {
    setInputCrpNameErrMsg("");
    setInputCrpEmailErrMsg("");
    setInputAddressErrMsg("");
    setInputCompNameErrMsg("");
    setInputCompWebsiteErrMsg("");
    setInputPrivacyLinkErrMsg("");
    setInputRegNumberErrMsg("");
    setInputCountryRegErrMsg("");
    setInputCorporateCompanyZip("");
    setInputRegistrationCourt("");
    setInputCorporateCompanyCity("");
    setInputCorporateCompanyState("");
    setInputCompanyEmailErrMsg("");
    setInputCountryCompanyErrMsg("");
  };

  const resetFormState = () => {
    setReadOnlyValue(true);
    setProfileReadOnly(true);
    setBusinessReadOnly(true);
    setShowSaveFlag(false);
    setShowBusinessFlag(false);
    setLoader(false);
    clearAllErrors();
  };

  const handleCancelProfile = () => {
    loadProfileFromDataExternal({
      data: profileData,
      setCorporateContactName,
      setCorporateContactEmail,
      setPhoneNumber,
      setAddress,
      setPrivacyPolicy,
      setCompanyRegNumber,
      setCountryOfReg,
      setCompanyName,
      setCompanyWebsite,
      setAgreementStatus,
      setAgreementDate,
      setRegistrationCourt,
      setCorporateCompanyState,
      setCorporateCompanyCity,
      setCorporateCompanyZip,
      setCountryOfCompany,
      setCompanyPhoneNumber,
      setCompanyEmail
    });
    resetFormState();
  };

  const handleAgreementChange = (event) => {
    const isChecked = event.target.checked;
    setAgreement_accepted(isChecked);
    const allValid = allBusinessValid && allProfileValid && isChecked;
    setDisableSubmitAgreement(!allValid);
  };

  const handleProfileSave = () => {
    handleProfileSaveExternal({
      corporateContactName,
      corporateContactEmail,
      phoneNumber,
      privacyPolicy,
      companyName,
      companyWebsite,
      address,
      corporateCompanyZip,
      corporateCompanyCity,
      corporateCompanyState,
      countryOfCompany,
      companyPhoneNumber,
      companyEmail,
      companyRegNumber,
      countryOfReg,
      registrationCourt,
      isDsaEditMode: location.state?.editMode ?? false,
      isSeller,
      isOemAdmin,
      getApiFunction,
      profileSection,
      setProfileData,
      setLoader,
      setReadOnlyValue,
      setProfileReadOnly,
      setShowSaveFlag,
      setBusinessReadOnly,
      setShowBusinessFlag,
      setSuccessNotification,
      setErrorMessage,
      setHeaderMessage,
      setOpenErrorPopup,
      handleProfileSaveSuccess
    });
  };

  const handleAcceptAgreement = async () => {
    await handleAcceptAgreementExternal({
      corporateContactName,
      corporateContactEmail,
      address,
      phoneNumber,
      privacyPolicy,
      companyRegNumber,
      countryOfReg,
      companyWebsite,
      companyName,
      corporateCompanyZip,
      corporateCompanyCity,
      corporateCompanyState,
      countryOfCompany,
      companyPhoneNumber,
      companyEmail,
      registrationCourt,
      agreementDetails,
      agreementDetailsData,
      isOemAdmin,
      setLoader,
      getConfig,
      acceptAgreement,
      dispatch,
      fetchAgreementDetails,
      APIManager,
      Moment,
      handleAcceptAgreementSuccess,
      handleAcceptAgreementError,
      setAgreementDetails,
      setAgreementStatus,
      setAgreementDate,
      setProfileData,
      setOpen,
      setErrorMessage,
      setHeaderMessage,
      setOpenErrorPopup
    });
  };

  const handleViewDeveloperAgreement = async () => {
    return handleViewDeveloperAgreementExternal({
      getConfig,
      dispatch,
      fetchAgreementDetails,
      allBusinessValid,
      allProfileValid,
      agreement_accepted,
      setDisableSubmitAgreement,
      setOpen
    });
  };

  
  const isProfileMandatoryValid = computeProfileMandatoryValid({
    isIdpLogin, corporateContactName, inputCrpNameErrMsg,
    corporateContactEmail, inputCrpEmailErrMsg,
    phoneNumber, address, inputAddressErrMsg,
    privacyPolicy, inputPrivacyLinkErrMsg
  });

  const isBusinessMandatoryValid = computeBusinessMandatoryValid({
    companyName, inputCompNameErrMsg, companyWebsite, inputCompWebsiteErrMsg,
    countryOfReg, inputCountryRegErrMsg, companyRegNumber, inputRegNumberErrMsg,
    registrationCourt, inputRegistrationCourt, corporateCompanyCity, inputCorporateCompanyCity,
    corporateCompanyState, inputCorporateCompanyState, corporateCompanyZip, inputCorporateCompanyZip,
    countryOfCompany, inputCountryCompanyErrMsg, companyPhoneNumber, companyEmail, inputCompanyEmailErrMsg
  });

  const sellerBusinessInvalid = isSeller && !isBusinessMandatoryValid;
  const isProfileSaveDisabled = !isProfileMandatoryValid || sellerBusinessInvalid;

  const isBusinessSaveDisabled = !(isProfileMandatoryValid && isBusinessMandatoryValid);

  useEffect(() => {
    setLoader(true);
    loadInitialData({
      isSeller,
      isOemAdmin,
      getApiFunction,
      getDataLoader,
      handleApiError,
      setProfileData,
      setAccount,
      setCorporateContactName,
      setCorporateContactEmail,
      setPhoneNumber,
      setAddress,
      setPrivacyPolicy,
      setCompanyRegNumber,
      setCountryOfReg,
      setCompanyName,
      setCompanyWebsite,
      setAgreementDetails,
      setAgreementStatus,
      setAgreementDate,
      setRegistrationCourt,
      setCorporateCompanyState,
      setCorporateCompanyCity,
      setCorporateCompanyZip,
      setCountryOfCompany,
      setCompanyPhoneNumber,
      setCompanyEmail,
      setComponentName: () => {},
      setErrorMessage,
      setHeaderMessage,
      setOpenErrorPopup,
      setLoader,
      setIsVwGroupSeller
    });
  }, []);

  useEffect(() => {
    if (!isSeller) return;
    dispatch(fetchAgreementDetails());
  }, [dispatch, isSeller]);
  
  useEffect(() => {
    if (!agreementDetailsData) return;
    setAgreementDetails(agreementDetailsData);
    setAgreementStatus(agreementDetailsData.status === "ACCEPTED");
    setAgreementDate(agreementDetailsData.statusDate || "");
  }, [agreementDetailsData]);



  const goToSection = useCallback((index) => {
    goToSectionExternal({
      index,
      readOnlyValue,
      setProfileSection,
      setBusinessSection,
      setShowBusinessFlag,
      setBusinessReadOnly,
      setProfileReadOnly,
      setShowSaveFlag,
      profileData,
      corporateContactName,
      corporateContactEmail,
      phoneNumber,
      address,
      companyRegNumber,
      countryOfReg,
      companyName,
      companyWebsite,
      privacyPolicy,
      registrationCourt,
      corporateCompanyState,
      corporateCompanyCity,
      corporateCompanyZip,
      countryOfCompany,
      companyPhoneNumber,
      companyEmail,
      setCorporateContactName,
      setCorporateContactEmail,
      setPhoneNumber,
      setAddress,
      setCompanyRegNumber,
      setCountryOfReg,
      setCompanyName,
      setCompanyWebsite,
      setPrivacyPolicy,
      setAgreementStatus,
      setAgreementDate,
      setRegistrationCourt,
      setCorporateCompanyState,
      setCorporateCompanyCity,
      setCorporateCompanyZip,
      setCountryOfCompany,
      setCompanyPhoneNumber,
      setCompanyEmail,
      setInputCrpNameErrMsg,
      setInputCrpEmailErrMsg,
      setInputAddressErrMsg,
      setInputCompNameErrMsg,
      setInputCompWebsiteErrMsg,
      setInputPrivacyLinkErrMsg,
      setInputRegNumberErrMsg,
      setInputCountryRegErrMsg,
      setInputCorporateCompanyZip,
      setInputRegistrationCourt,
      setInputCorporateCompanyCity,
      setInputCorporateCompanyState,
      setInputCompanyEmailErrMsg,
      setInputCountryCompanyErrMsg,
    });
  }, [
    readOnlyValue,
    profileData,
    corporateContactName,
    corporateContactEmail,
    phoneNumber,
    address,
    companyRegNumber,
    countryOfReg,
    companyName,
    companyWebsite,
    privacyPolicy,
    registrationCourt,
    corporateCompanyState,
    corporateCompanyCity,
    corporateCompanyZip,
    countryOfCompany,
    companyPhoneNumber,
    companyEmail
  ]);

  useEffect(() => {
    dispatch(fetchCountriesDetails());
  }, []);

  const activeAgreement = agreementDetails || agreementDetailsData;
  const isNewUpdateOrAvailable = activeAgreement?.status === 'NEW_UPDATE_AVAILABLE' || activeAgreement?.isNewAgreementAvailable;
  const showAcceptedInfo = isNewUpdateOrAvailable && agreementStatus;

  return (
    <>
      <div className="appListContainer">
        <div>
          <Topheader />
        </div>

        {loader && (
          <Backdrop
            sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
            open={loader}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
        <div className="wholeContainer">
          <div className="backToApplication">
            {" "}
            {!showSaveFlag && !showBusinessFlag && (
              <div style={{ marginRight: "20px" }}>
                <button
                  type="button"
                  className={profileSection ? "activeSection menu-section" : "menu-section"}
                  style={{ width: "279px", height: "20px", marginBottom: "10px" }}
                  onClick={() => goToSection(1)}
                >
                  <Typography
                    sx={{ color: profileSection ? "#0476e2" : "#2A2C2D" }}
                  >
                    Profile Info
                  </Typography>
                </button>
                {isSeller && (
                  <button
                    type="button"
                    className={businessSection ? "activeSection menu-section" : "menu-section"}
                    style={{ width: "279px", height: "20px" }}
                    onClick={() => goToSection(2)}
                  >
                    <Typography
                      sx={{ color: businessSection ? "#0476e2" : "#2A2C2D" }}
                    >
                      Business Info
                    </Typography>
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="containercls">
            <div className="contentCls">
              <UserProfileHeader
                isSeller={isSeller}
                showSaveFlag={showSaveFlag}
                showBusinessFlag={showBusinessFlag}
                profileSection={profileSection}
                businessSection={businessSection}
                handleEdit={handleEdit}
                handleCancelProfile={handleCancelProfile}
                handleProfileSave={handleProfileSave}
                isProfileSaveDisabled={isProfileSaveDisabled}
                isBusinessSaveDisabled={isBusinessSaveDisabled}
                goToSection={goToSection}
                isIdpLogin={sessionStorage.getItem("idpLogin") === "true"}
                isEditDisabled={isVwGroupSeller}
              />
              <div>
                <form>
                  <ProfileFormSection
                    profileSection={profileSection}
                    account={account}
                    businessReadOnly={businessReadOnly}
                    companyName={companyName}
                    isOemAdmin={isOemAdmin}
                    handleFieldChange={handleFieldChange}
                    inputCompNameErrMsg={inputCompNameErrMsg}
                    profileReadOnly={profileReadOnly}
                    corporateContactName={corporateContactName}
                    inputCrpNameErrMsg={inputCrpNameErrMsg}
                    companyWebsite={companyWebsite}
                    inputCompWebsiteErrMsg={inputCompWebsiteErrMsg}
                    corporateContactEmail={corporateContactEmail}
                    inputCrpEmailErrMsg={inputCrpEmailErrMsg}
                    registrationCourt={registrationCourt}
                    inputRegistrationCourt={inputRegistrationCourt}
                    phoneNumber={phoneNumber}
                    setPhoneNumber={setPhoneNumber}
                    companyRegNumber={companyRegNumber}
                    inputRegNumberErrMsg={inputRegNumberErrMsg}
                    readOnlyValue={readOnlyValue}
                    privacyPolicy={privacyPolicy}
                    inputPrivacyLinkErrMsg={inputPrivacyLinkErrMsg}
                    showSaveFlag={showSaveFlag}
                    showBusinessFlag={showBusinessFlag}
                    agreementDetails={agreementDetails}
                    agreementDetailsData={agreementDetailsData}
                    agreementStatus={agreementStatus}
                    handleViewDeveloperAgreement={handleViewDeveloperAgreement}
                    setCompanyName={setCompanyName}
                    setCorporateContactName={setCorporateContactName}
                    setCompanyWebsite={setCompanyWebsite}
                    setCorporateContactEmail={setCorporateContactEmail}
                    setRegistrationCourt={setRegistrationCourt}
                    setCompanyRegNumber={setCompanyRegNumber}
                    setPrivacyPolicy={setPrivacyPolicy}
                  />
                  <BusinessFormSection
                    businessSection={businessSection}
                    businessReadOnly={businessReadOnly}
                    countryOfReg={countryOfReg}
                    handleCountryChange={handleCountryChange}
                    countries={countries}
                    inputCountryRegErrMsg={inputCountryRegErrMsg}
                    address={address}
                    handleFieldChange={handleFieldChange}
                    inputAddressErrMsg={inputAddressErrMsg}
                    corporateCompanyCity={corporateCompanyCity}
                    inputCorporateCompanyCity={inputCorporateCompanyCity}
                    corporateCompanyState={corporateCompanyState}
                    inputCorporateCompanyState={inputCorporateCompanyState}
                    isOemAdmin={isOemAdmin}
                    corporateCompanyZip={corporateCompanyZip}
                    inputCorporateCompanyZip={inputCorporateCompanyZip}
                    countryOfCompany={countryOfCompany}
                    inputCountryCompanyErrMsg={inputCountryCompanyErrMsg}
                    companyPhoneNumber={companyPhoneNumber}
                    setCompanyPhoneNumber={setCompanyPhoneNumber}
                    companyEmail={companyEmail}
                    inputCompanyEmailErrMsg={inputCompanyEmailErrMsg}
                    readOnlyValue={readOnlyValue}
                    privacyPolicy={privacyPolicy}
                    inputPrivacyLinkErrMsg={inputPrivacyLinkErrMsg}
                    agreementDetails={agreementDetails}
                    agreementDetailsData={agreementDetailsData}
                    agreementStatus={agreementStatus}
                    handleViewDeveloperAgreement={handleViewDeveloperAgreement}
                    setAddress={setAddress}
                    setCorporateCompanyCity={setCorporateCompanyCity}
                    setCorporateCompanyState={setCorporateCompanyState}
                    setCorporateCompanyZip={setCorporateCompanyZip}
                    setCompanyEmail={setCompanyEmail}
                    setPrivacyPolicy={setPrivacyPolicy}
                  />
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={open}
        maxWidth="md"
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            Marketplace Developer Agreement{" "}
            {agreementStatus && <CheckIcon className="checkAcceptedCls" sx={{ width: 14, height: 14 }} />}
          </div>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            style={{ color: '#666' }}
          >
            <CloseIcon sx={{ width: 32, height: 32 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DeveloperAgreement
            versionTitle={activeAgreement?.version || null}
            uploadedDate={activeAgreement?.uploadedDate?.slice(0, 10) || null}
            status={activeAgreement?.status || null}
            agreementId={activeAgreement?.agreementId || null}
            pdfUrl={activeAgreement?.url || null}
            isDefault={activeAgreement?.isDefault || false}
            isNewAgreementAvailable={activeAgreement?.isNewAgreementAvailable || false}
            acceptedVersion={showAcceptedInfo ? activeAgreement?.previousVersion || activeAgreement?.version : null}
            acceptedDate={showAcceptedInfo ? agreementDate : null}
          />
        </DialogContent>

        {renderDialogActions({
          agreement_accepted,
          disableSubmitAgreement,
          allBusinessValid,
          allProfileValid,
          agreementDetails,
          agreementDetailsData,
          agreementStatus,
          handleAgreementChange,
          handleDisagree,
          handleAcceptAgreement
        })}
        {renderAcceptedDialog({
          agreementDetails,
          agreementDetailsData,
          agreementStatus,
          agreementDate,
          Moment
        })}
      </Dialog>

      <PopupDialog
        openErrorPopup={openErrorPopup}
        errorMessage={errorMessage}
        headerMessage={headerMessage}
        onClose={() => setOpenErrorPopup(false)}
      />

      <Snackbar
        open={successNotification !== ''}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setSuccessNotification('')}
      >
        <div style={{ background: 'linear-gradient(90deg, #BEF8DF 0%, #72E8B1 100%)', padding: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
          {successNotification === 'seller' ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong>Congratulations!</strong>
                <IconButton size="small" aria-label="close" onClick={() => setSuccessNotification('')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
              <div>You are all set to access your developer access.</div>
            </>
          ) : (
            
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>User profile is saved successfully</span>
                <IconButton size="small" aria-label="close" onClick={() => setSuccessNotification('')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            
          )}
        </div>
      </Snackbar>
    </>
  );
}